import React, { useState } from 'react';

// 🔧 Support both LLM output format and normalized format
interface Phase {
  // LLM output fields (snake_case from database)
  phase_order?: number;
  phase_type?: string;
  phase_name?: string;
  duration_seconds?: number;
  name?: string; // Alternative field for phase_type
  type?: string; // Alternative field for phase_type
  target_pace_min_per_km?: string | number; // Pace target from database
  target_hr_avg?: number; // Average HR target
  target_hr_min?: number; // Minimum HR target
  target_hr_max?: number; // Maximum HR target
  
  // Normalized fields (for sessionDataAdapter)
  phaseNumber?: number;
  phaseType?: string;
  prescribedPace?: number;
  actualPace?: number;
  prescribedDuration?: number;
  actualDuration?: number;
  prescribedDistance?: number;
  actualDistance?: number;
  avgHR?: number;
  minHR?: number; // Added for minHR access
  maxHR?: number;
  adherence?: number; // 0-100%
  instructions?: string;
}

interface ExpandablePhaseBreakdownProps {
  phases: Phase[];
}

export const ExpandablePhaseBreakdown: React.FC<ExpandablePhaseBreakdownProps> = ({ phases }) => {
  // 🔧 Helper: Convert pace string ("5:55") to seconds (355)
  const paceStringToSeconds = (paceStr: string | number | undefined): number | undefined => {
    if (typeof paceStr === 'number') return paceStr; // Already in seconds
    if (!paceStr || typeof paceStr !== 'string') return undefined;
    const parts = paceStr.split(':');
    if (parts.length !== 2) return undefined;
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    if (isNaN(minutes) || isNaN(seconds)) return undefined;
    return minutes * 60 + seconds;
  };

  // 🔧 Helper: Get friendly phase type label
  const getPhaseLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'warmup': 'Warmup',
      'warm-up': 'Warmup',
      'cooldown': 'Cooldown',
      'cool-down': 'Cooldown',
      'steady': 'Steady',
      'tempo': 'Tempo',
      'intervals': 'Intervals',
      'recovery': 'Recovery',
      'main': 'Main Set',
      'work': 'Work',
      'rest': 'Rest',
      'unknown': 'Phase'
    };
    return labels[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  // 🔧 Normalize phase data to handle both formats (database vs device)
  const normalizedPhases = phases.map((phase, index) => ({
    phaseNumber: phase.phaseNumber || phase.phase_order || (index + 1),
    phaseType: phase.phaseType || phase.phase_type || phase.type || phase.name || 'unknown',
    phaseName: phase.phase_name || phase.phaseType || phase.phase_type || phase.type || phase.name,
    prescribedDuration: phase.prescribedDuration || phase.duration_seconds,
    actualDuration: phase.actualDuration,
    prescribedPace: phase.prescribedPace || paceStringToSeconds(phase.target_pace_min_per_km),
    actualPace: phase.actualPace,
    prescribedDistance: phase.prescribedDistance,
    actualDistance: phase.actualDistance,
    avgHR: phase.avgHR || phase.target_hr_avg,
    minHR: phase.minHR || phase.target_hr_min,
    maxHR: phase.maxHR || phase.target_hr_max,
    adherence: phase.adherence,
    instructions: phase.instructions
  }));
  const [isExpanded, setIsExpanded] = useState(false);

  const formatPace = (secondsPerKm: number): string => {
    if (!secondsPerKm || !isFinite(secondsPerKm)) return '--:--';
    const minutes = Math.floor(secondsPerKm / 60);
    const seconds = Math.floor(secondsPerKm % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${mins}min`;
  };

  // 🆕 FIX #2: Renamed from "adherence" to "performance" for clarity
  const getPerformanceIcon = (performance?: number): string => {
    if (!performance) return '⚪';
    if (performance >= 95) return '✅';
    if (performance >= 85) return '⚠️';
    return '❌';
  };

  const getPerformanceColor = (performance?: number): string => {
    if (!performance) return 'text-gray-400';
    if (performance >= 95) return 'text-green-400';
    if (performance >= 85) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <details 
      className="w-full max-w-2xl mx-auto mb-4"
      open={isExpanded}
      onToggle={(e) => setIsExpanded((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-center text-sm font-medium text-blue-400 hover:text-blue-300 py-2 px-4 bg-gray-800 rounded-lg transition-colors">
        Show Phase Breakdown ({normalizedPhases.length} phases) ▼
      </summary>
      
      <div className="mt-3 bg-gray-800 rounded-lg p-4 space-y-3">
        {normalizedPhases.map((phase, idx) => (
          <div 
            key={idx}
            className="border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-white">
                  {getPhaseLabel(phase.phaseType || 'unknown')}
                </span>
                {phase.adherence !== undefined && (
                  <span className={`text-sm ${getPerformanceColor(phase.adherence)}`}>
                    {getPerformanceIcon(phase.adherence)} {Math.round(phase.adherence)}%
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400">Phase {phase.phaseNumber}</span>
            </div>
            
            {/* Phase Details Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {/* Duration */}
              {(phase.prescribedDuration || phase.actualDuration) && (
                <div>
                  <div className="text-gray-400 text-xs mb-1">Duration</div>
                  <div className="text-white">
                    {phase.actualDuration ? formatDuration(phase.actualDuration) : '--'}
                    {phase.prescribedDuration && (
                      <span className="text-gray-500 ml-1">
                        / {formatDuration(phase.prescribedDuration)}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Distance */}
              {(phase.prescribedDistance || phase.actualDistance) && (
                <div>
                  <div className="text-gray-400 text-xs mb-1">Distance</div>
                  <div className="text-white">
                    {phase.actualDistance ? `${(phase.actualDistance / 1000).toFixed(2)}km` : '--'}
                    {phase.prescribedDistance && (
                      <span className="text-gray-500 ml-1">
                        / {(phase.prescribedDistance / 1000).toFixed(2)}km
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Pace */}
              {(phase.prescribedPace || phase.actualPace) && (
                <div>
                  <div className="text-gray-400 text-xs mb-1">Pace</div>
                  <div className="text-white">
                    {phase.actualPace ? formatPace(phase.actualPace) : '--'}
                    {phase.prescribedPace && (
                      <span className="text-gray-500 ml-1">
                        / {formatPace(phase.prescribedPace)}
                      </span>
                    )}
                    <span className="text-xs text-gray-500"> /km</span>
                  </div>
                </div>
              )}
              
              {/* Heart Rate */}
              {(phase.avgHR || phase.maxHR || phase.minHR) && (
                <div>
                  <div className="text-gray-400 text-xs mb-1">Heart Rate</div>
                  <div className="text-white">
                    {/* Actual HR (post-completion) */}
                    {phase.avgHR && `${phase.avgHR} avg`}
                    {phase.avgHR && phase.maxHR && ' / '}
                    {phase.maxHR && `${phase.maxHR} max`}
                    
                    {/* Prescribed HR range (pre-completion) */}
                    {!phase.avgHR && (phase.minHR || phase.maxHR) && (
                      <span className="text-gray-500">
                        {phase.minHR && phase.maxHR 
                          ? `${phase.minHR}-${phase.maxHR} bpm` 
                          : phase.minHR 
                            ? `${phase.minHR}+ bpm` 
                            : `${phase.maxHR} bpm max`}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Instructions */}
            {phase.instructions && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <div className="text-xs text-gray-400 mb-1">Instructions</div>
                <div className="text-sm text-gray-300">{phase.instructions}</div>
              </div>
            )}
          </div>
        ))}
        
        {/* Overall Summary - 🆕 AUDIT FIX JAN 19: Only show performance for POST-completion */}
        {(() => {
          // Check if phases have actual adherence data (post-completion)
          const hasPerformanceData = phases.some(p => 
            p.adherence !== undefined && p.adherence !== null && p.adherence > 0
          );
          
          if (!hasPerformanceData) return null; // Pre-completion - don't show 0%
          
          return (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="text-center">
                <div className="text-sm text-gray-400">Workout Performance</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {Math.round(phases.reduce((acc, p) => acc + (p.adherence || 0), 0) / phases.length)}%
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {phases.filter(p => (p.adherence || 0) >= 95).length} / {phases.length} phases on target
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </details>
  );
};

